import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import RightPanel from '../components/RightPanel';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const WealthFlowPage = () => {
  const [showPanel, setShowPanel] = useState(false);

  useEffect(() => {
    const handleDoubleClick = () => {
      setShowPanel(false);
    };

    document.addEventListener('dblclick', handleDoubleClick);
    return () => {
      document.removeEventListener('dblclick', handleDoubleClick);
    };
  }, []);

  const handleHeaderClick = () => {
    setShowPanel(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="財富流系統 WealthFlow｜核心財商與人生全景決策引擎"
        description="財富流系統（WealthFlow）為明悅文化打造的沉浸式遊戲化（Game-based）財商學習與賦能引擎，於 2 小時內模擬 40 年人生決策路徑，協助企業與個人提升高能動性 High Agency，活出富而喜悅的成長之路。"
        path="/wealth-flow"
        keywords="財富流, WealthFlow, 財商, 遊戲化學習, Game-based Learning, 高能動性, High Agency, 賦能, 成長, 學習, 企業教練, 明悅文化, 明悅心創"
      />
      <Header onHeaderClick={handleHeaderClick} />
      <RightPanel isVisible={showPanel} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-5xl lg:text-6xl font-serif text-gray-900 mb-6">
                財富流系統（WealthFlow System）
              </h1>
              <h2 className="text-3xl text-orange-600 font-semibold mb-8">
                核心財商與人生的全景決策引擎
              </h2>
              <p className="text-xl text-gray-600 italic">
                The Core Wealth & Life Optimization Engine
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-xl mb-12">
              <p className="text-lg text-gray-700 leading-relaxed">
                將<span className="font-bold text-orange-600">「東方傳統智慧」</span>與<span className="font-bold text-orange-600">「西方財富邏輯」</span>完美融合的沉浸式商業與人生模擬平台。系統精準模擬 20 至 60 歲黃金創富期的關鍵決策路徑，將時間、精力、人脈、能力、金錢五大核心資本量化。透過獨創的「逆流、平流、順流」三層架構演練，協助經理人與創業家在低成本的沙盒環境中盲點解碼，全面升級財務智商（FQ）、逆境控管（AQ）與自我覺察力，快速找到從財務自由走向富而喜悅的實踐路徑。
              </p>
            </div>

            {/* First Image - Two side-by-side photos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_instant-website-93/artifacts/a9eo8k6a_%E8%B2%A1%E5%AF%8C%E6%B5%81.jpg"
                  alt="財富流遊戲沙盤近景"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_instant-website-93/artifacts/rxsftjmn_%E8%B2%A1%E5%AF%8C%E6%B5%812.JPG"
                  alt="財富流遊戲沙盤桌面全景"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Positioning Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif text-gray-900 mb-4">產品定位與核心價值</h2>
              <p className="text-2xl text-orange-500 font-medium">Product Positioning & Core Values</p>
            </div>

            <div className="bg-gradient-to-r from-orange-400 to-red-400 rounded-2xl p-8 mb-12 text-center">
              <p className="text-2xl font-bold text-white">
                「在低成本的沙盒環境中精準試錯，打造高能動性的組織與個人決策全景圖。」
              </p>
            </div>

            <div className="space-y-6 mb-12">
              <p className="text-lg text-gray-700 leading-relaxed">
                在變革加劇的商業環境中，企業與個人常陷入「高產出、低效能」或「資源錯置」的戰略困局。<span className="font-bold text-orange-600">財富流系統（WealthFlow System）</span>是一套結合行為經濟學、系統思考與認知心理學的「組織與個人決策飛行模擬器」。
              </p>

              <p className="text-lg text-gray-700 leading-relaxed">
                透過高擬真的<span className="font-bold text-orange-600">沉浸式敏捷推演（Agile Simulation）</span>，參與者在 2 小時內將歷經跨越 40 年的商業與生命週期。系統全面模擬職涯發展、資本擴張、市場波動、黑天鵝事件及逆境風險。它不只是一套財商升級工具，更是一套教導決策者在時間、精力、人脈、能力與資金五大核心資本之間，建立最佳化配置（Asset Allocation）的人生效率引擎。
              </p>
            </div>

            {/* Core Business Value */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-orange-600 mb-6">核心商業價值 (Core Business Value)：</h3>
              
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-8 border-l-4 border-orange-400">
                  <h4 className="text-xl font-bold text-gray-800 mb-3">
                    多維資本流動認知 (Multidimensional Capital Configuration)
                  </h4>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    協助參與者打破對財富的狹隘財務定義，深刻理解組織與個人效能來自於五大核心資本（時間、精力、人脈、能力、資金）的動態平衡與高效流動。
                  </p>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-8 border-l-4 border-orange-400">
                  <h4 className="text-xl font-bold text-gray-800 mb-3">
                    決策盲點解碼 (Cognitive Bias Decoupling)
                  </h4>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    透過高頻次的決策反饋，協助個體在無風險的沙盒中看見認知盲點，重塑限制性信念，從物質層面的「富」跨越至精神與物質和諧共生的「富而喜悅」生態格局。
                  </p>
                </div>
              </div>
            </div>

            {/* Key Scenarios */}
            <div>
              <h3 className="text-2xl font-bold text-orange-600 mb-6">關鍵應用場景 (Key Scenarios)：</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border-2 border-orange-200">
                  <p className="text-lg text-gray-800 font-medium">
                    高階商務社交與投資者關係維護 (High-end Business Networking)
                  </p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border-2 border-orange-200">
                  <p className="text-lg text-gray-800 font-medium">
                    企業戰略共識營與高階團隊建設 (Corporate Strategy & Teambuilding)
                  </p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border-2 border-orange-200">
                  <p className="text-lg text-gray-800 font-medium">
                    企業人才盤點、高潛力人才（HiPo）評估與領導力鑑別 (Talent Assessment & Succession Planning)
                  </p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border-2 border-orange-200">
                  <p className="text-lg text-gray-800 font-medium">
                    全方位財商思維（FQ）與跨界決策力提升課程 (Financial Intelligence & Decision-Making Training)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* System Mechanics Section */}
      <section className="py-16 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif text-gray-900 mb-4">系統運作原理</h2>
              <p className="text-2xl text-orange-500 font-medium">System Mechanics & Framework</p>
            </div>

            <div className="bg-gradient-to-r from-orange-400 to-red-400 rounded-2xl p-8 mb-12 text-center">
              <p className="text-2xl font-bold text-white">
                「以六大商數為底層架構，構建『人生遊戲化（Gamification of Life）』的精密決策模型。」
              </p>
            </div>

            {/* Second Image - Core Quotient System */}
            <div className="bg-white rounded-2xl p-6 shadow-xl mb-12">
              <img 
                src="https://customer-assets.emergentagent.com/job_cultural-gateway-2/artifacts/gf5e3g4z_%E8%B2%A1%E5%AF%8C%E6%B5%81.png"
                alt="核心商數與人生系統對照表"
                className="w-full h-auto rounded-xl"
              />
            </div>

            <div className="space-y-6 mb-12">
              <p className="text-lg text-gray-700 leading-relaxed">
                財富流系統的運作機制建立在一個將複雜人生智慧量化、結構化的精密商業模型之上。系統將個體與組織的核心競爭力提煉為<span className="font-bold text-orange-600">「六大商數（HQ、EQ、FQ、AQ、PQ、SQ）」</span>，並透過六大核心指標系統進行動態追蹤與演練：
              </p>

              {/* System Flow Diagram */}
              <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
                <div className="space-y-6">
                  <div className="text-3xl font-bold text-orange-600">▼</div>
                  <div className="bg-gradient-to-r from-orange-100 to-yellow-100 rounded-xl p-6">
                    <p className="text-xl font-bold text-gray-800">
                      【 2小時敏捷推演：40年動態資本配置與決策權衡 】
                    </p>
                  </div>
                  <div className="text-3xl font-bold text-orange-600">▼</div>
                  <div className="bg-gradient-to-r from-orange-400 to-red-400 rounded-xl p-6">
                    <p className="text-xl font-bold text-white">
                      【 財務自由突破點：被動收入 ＞ 總支出 】──► 進入「富而喜悅」高能順流層
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Simulation Flow & Strategic Objective */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h4 className="text-xl font-bold text-orange-600 mb-4">
                  敏捷推演流程 (The Simulation Flow)
                </h4>
                <p className="text-lg text-gray-700 leading-relaxed">
                  參與者在 2 小時的沉浸式推演中，面臨橫跨 40 年的職涯與市場週期。每一輪的資源投入與決策，都需要在六大指標系統中進行高維度的權衡與選擇。每一次外部環境的順遂（市場紅利）或逆境（黑天鵝事件），都在即時考驗並拉高參與者的「動態覺察力（Real-time Awareness）」。
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h4 className="text-xl font-bold text-orange-600 mb-4">
                  終極戰略目標 (Ultimate Strategic Objective)
                </h4>
                <p className="text-lg text-gray-700 leading-relaxed">
                  推演的勝負關鍵在於參與者能否透過精準的資產配置，實現「被動收入大於總支出」的財務自由狀態。唯有突破此臨界點，方能晉升至象徵高效能、高動能的「順流層（Flow State）」，在實現終極願景的同時，實踐物質與精神雙軌並進的「富而喜悅」最高境界。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Multidimensional Value Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-serif text-gray-900 mb-4">多維商業價值與社會意義</h2>
              <p className="text-2xl text-orange-500 font-medium">Multidimensional Value & CSR Impact</p>
            </div>

            <div className="space-y-12">
              {/* Corporate Value */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-4xl">💡</span>
                  <h3 className="text-2xl font-bold text-gray-900">對企業發展的價值</h3>
                </div>
                <p className="text-lg text-orange-600 font-semibold mb-4">Corporate Value & Talent Development</p>
                
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6">
                    <h4 className="text-xl font-bold text-gray-800 mb-3">
                      高潛力人才鑑別工具 (Predictive Talent Analytics)
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      在高度擬真的壓力沙盤中，團隊成員的真實領導力風格、風險偏好、協作模式與危機決策行為將會「完全顯性化」。這為企業提供了客觀、動態的人才觀測數據，是企業進行內部人才盤點、繼任者計劃（Succession Planning）的高效工具。
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6">
                    <h4 className="text-xl font-bold text-gray-800 mb-3">
                      組織能動性與共識落地 (Organizational Agency & Culture Alignment)
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      透過遊戲化（Gamification）的情景實戰，消除跨部門溝通壁壘，凝聚團隊戰略共識，並將企業文化（如誠信、敏捷、冒險精神）內化為員工的自發決策行為，全面提升組織韌性與績效產出。
                    </p>
                  </div>
                </div>
              </div>

              {/* Individual Growth */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-4xl">💡</span>
                  <h3 className="text-2xl font-bold text-gray-900">對個人成長與財商認知的價值</h3>
                </div>
                <p className="text-lg text-orange-600 font-semibold mb-4">Individual Growth & Financial Intelligence</p>
                
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6">
                    <h4 className="text-xl font-bold text-gray-800 mb-3">
                      個人戰略卡點解碼 (Personal Strategic Bottleneck Identification)
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      協助經理人清晰透視自身在金錢管理、人際關係、事業佈局與身心健康上的「認知限制」，打破限制性信念（Limiting Beliefs），提升應對商業與人生不確定性（VUCA時代）的抗壓韌性。
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6">
                    <h4 className="text-xl font-bold text-gray-800 mb-3">
                      實戰化財商思維內化 (Experiential Financial Intelligence Upgrade)
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      將抽象且枯燥的財務工具（如現金流結構、資產負債表優化、投資報酬率 ROI 計算）轉化為直覺式的模擬體驗。參與者在推演中學習如何高效槓桿核心資源，建立系統化的資本思維。
                    </p>
                  </div>
                </div>
              </div>

              {/* Future Education */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-4xl">💡</span>
                  <h3 className="text-2xl font-bold text-gray-900">對未來教育與人才育成的價值</h3>
                </div>
                <p className="text-lg text-orange-600 font-semibold mb-4">Next-Gen Talent Blueprint</p>
                
                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-gray-800 mb-3">
                    無風險人生實驗室 (Risk-Free Future Laboratory)
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    為青少年及新世代提供一個低成本、高回饋的社會與經濟模擬環境。協助他們提早理解市場經濟規律、釐清個人願景、規劃職涯路徑，並在過程中建立健康的價值觀與財富觀，為未來培養具備「高能動性（High Agency）」的未來領袖。
                  </p>
                </div>
              </div>

              {/* Social Impact */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span className="text-4xl">💡</span>
                  <h3 className="text-2xl font-bold text-gray-900">社會價值與企業社會責任</h3>
                </div>
                <p className="text-lg text-orange-600 font-semibold mb-4">Social Impact & CSR</p>
                
                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6">
                  <h4 className="text-xl font-bold text-gray-800 mb-3">
                    引領永續經濟生態 (Driving Sustainable Economic Well-being)
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    明悅心創透過財富流系統，引導大眾與企業從單純追求物質財富（Linear Growth），走向物質與精神和諧統一的「富而喜悅」生命哲學（Exponential Well-being）。這不僅能提升個人的幸福感，更能為構建健康、永續且具備高度韌性的社會經濟生態圈做出核心貢獻。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WealthFlowPage;
